import { useState, useEffect, useRef, useCallback } from 'react';

interface EmergencyData {
  id: string;
  patientId: string;
  patientName: string;
  startTime: string;
  status: 'active' | 'en_route' | 'arrived' | 'completed';
  location?: string;
  ambulanceId?: string;
  lastUpdate: number;
}

interface AmbulanceData {
  id: string;
  status: string;
  location: string;
  eta: string;
  distance: string;
  speed: string;
  lastUpdate: number;
}

interface CachedData<T> {
  data: T;
  timestamp: number;
  ttl: number; // Time to live in milliseconds
}

interface ConnectionStatus {
  isConnected: boolean;
  lastHeartbeat: number;
  reconnectAttempts: number;
}

// Cache TTL constants (in milliseconds)
const CACHE_TTL = {
  EMERGENCY_DATA: 30 * 1000, // 30 seconds
  AMBULANCE_DATA: 10 * 1000, // 10 seconds
  PATIENT_DATA: 60 * 1000,   // 1 minute
  SYMPTOM_REPORTS: 30 * 1000  // 30 seconds
};

// Simulated WebSocket for development (replace with real WebSocket in production)
class RealTimeDataManager {
  private static instance: RealTimeDataManager;
  private cache: Map<string, CachedData<any>> = new Map();
  private subscribers: Map<string, Set<(data: any) => void>> = new Map();
  private connection: ConnectionStatus = {
    isConnected: false,
    lastHeartbeat: 0,
    reconnectAttempts: 0
  };
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private reconnectTimeout: NodeJS.Timeout | null = null;

  static getInstance(): RealTimeDataManager {
    if (!RealTimeDataManager.instance) {
      RealTimeDataManager.instance = new RealTimeDataManager();
    }
    return RealTimeDataManager.instance;
  }

  // Initialize connection (simulated WebSocket)
  connect(): void {
    if (this.connection.isConnected) return;

    console.log('🔗 Connecting to real-time data service...');
    
    // Simulate connection establishment
    setTimeout(() => {
      this.connection.isConnected = true;
      this.connection.lastHeartbeat = Date.now();
      this.connection.reconnectAttempts = 0;
      
      console.log('✅ Connected to real-time data service');
      
      // Start heartbeat
      this.startHeartbeat();
      
      // Notify connection status subscribers
      this.notifySubscribers('connection-status', this.connection);
      
      // Start simulated real-time updates
      this.startSimulatedUpdates();
    }, 1000);
  }

  disconnect(): void {
    this.connection.isConnected = false;
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    console.log('🔌 Disconnected from real-time data service');
  }

  private startHeartbeat(): void {
    this.heartbeatInterval = setInterval(() => {
      if (this.connection.isConnected) {
        this.connection.lastHeartbeat = Date.now();
        this.notifySubscribers('connection-status', this.connection);
      }
    }, 5000); // Heartbeat every 5 seconds
  }

  private startSimulatedUpdates(): void {
    // Simulate real-time emergency updates
    setInterval(() => {
      if (this.connection.isConnected) {
        this.simulateEmergencyUpdate();
      }
    }, 2000); // Update every 2 seconds

    // Simulate ambulance location updates
    setInterval(() => {
      if (this.connection.isConnected) {
        this.simulateAmbulanceUpdate();
      }
    }, 5000); // Update every 5 seconds
  }

  private simulateEmergencyUpdate(): void {
    const emergencyData: EmergencyData = {
      id: 'EMG001',
      patientId: '1',
      patientName: 'Juan Pérez',
      startTime: new Date(Date.now() - 12 * 60 * 1000).toISOString(),
      status: 'active',
      location: 'Chillán Centro',
      ambulanceId: 'AMB-07',
      lastUpdate: Date.now()
    };

    this.updateCache('emergency-EMG001', emergencyData, CACHE_TTL.EMERGENCY_DATA);
    this.notifySubscribers('emergency-EMG001', emergencyData);
  }

  private simulateAmbulanceUpdate(): void {
    const ambulanceData: AmbulanceData = {
      id: 'AMB-07',
      status: 'en_route_to_patient',
      location: 'Av. Brasil con O\'Higgins',
      eta: `${Math.max(1, Math.floor(Math.random() * 8) + 3)} minutos`,
      distance: `${(Math.random() * 3 + 1).toFixed(1)} km`,
      speed: `${Math.floor(Math.random() * 20) + 35} km/h`,
      lastUpdate: Date.now()
    };

    this.updateCache('ambulance-AMB-07', ambulanceData, CACHE_TTL.AMBULANCE_DATA);
    this.notifySubscribers('ambulance-AMB-07', ambulanceData);
  }

  // Cache management
  private updateCache<T>(key: string, data: T, ttl: number): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }

  private getCachedData<T>(key: string): T | null {
    const cached = this.cache.get(key);
    if (!cached) return null;

    const now = Date.now();
    if (now - cached.timestamp > cached.ttl) {
      this.cache.delete(key);
      return null;
    }

    return cached.data as T;
  }

  // Subscription management
  subscribe<T>(channel: string, callback: (data: T) => void): () => void {
    if (!this.subscribers.has(channel)) {
      this.subscribers.set(channel, new Set());
    }
    
    this.subscribers.get(channel)!.add(callback);

    // Return unsubscribe function
    return () => {
      const channelSubscribers = this.subscribers.get(channel);
      if (channelSubscribers) {
        channelSubscribers.delete(callback);
        if (channelSubscribers.size === 0) {
          this.subscribers.delete(channel);
        }
      }
    };
  }

  private notifySubscribers<T>(channel: string, data: T): void {
    const channelSubscribers = this.subscribers.get(channel);
    if (channelSubscribers) {
      channelSubscribers.forEach(callback => callback(data));
    }
  }

  // Public API methods
  async fetchData<T>(key: string, fallbackFetch?: () => Promise<T>): Promise<T | null> {
    // Try to get from cache first
    const cached = this.getCachedData<T>(key);
    if (cached) {
      console.log(`📦 Cache hit for ${key}`);
      return cached;
    }

    // If not in cache and we have a fallback, use it
    if (fallbackFetch) {
      try {
        console.log(`🌐 Cache miss for ${key}, fetching...`);
        const data = await fallbackFetch();
        
        // Determine TTL based on data type
        const ttl = key.includes('emergency') ? CACHE_TTL.EMERGENCY_DATA :
                   key.includes('ambulance') ? CACHE_TTL.AMBULANCE_DATA :
                   key.includes('patient') ? CACHE_TTL.PATIENT_DATA :
                   CACHE_TTL.SYMPTOM_REPORTS;
        
        this.updateCache(key, data, ttl);
        return data;
      } catch (error) {
        console.error(`❌ Failed to fetch ${key}:`, error);
        return null;
      }
    }

    return null;
  }

  getConnectionStatus(): ConnectionStatus {
    return { ...this.connection };
  }

  // Cleanup
  cleanup(): void {
    this.disconnect();
    this.cache.clear();
    this.subscribers.clear();
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }
  }
}

// React hook for using real-time data
export const useRealTimeData = <T>(
  channel: string, 
  initialData?: T,
  fallbackFetch?: () => Promise<T>
) => {
  const [data, setData] = useState<T | null>(initialData || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<number>(0);
  const managerRef = useRef<RealTimeDataManager>();

  useEffect(() => {
    managerRef.current = RealTimeDataManager.getInstance();
    
    // Connect if not already connected
    if (!managerRef.current.getConnectionStatus().isConnected) {
      managerRef.current.connect();
    }

    // Subscribe to data updates
    const unsubscribe = managerRef.current.subscribe<T>(channel, (newData) => {
      setData(newData);
      setLastUpdate(Date.now());
      setLoading(false);
      setError(null);
    });

    // Initial data fetch
    const fetchInitialData = async () => {
      try {
        const initialFetchedData = await managerRef.current!.fetchData(channel, fallbackFetch);
        if (initialFetchedData) {
          setData(initialFetchedData);
          setLastUpdate(Date.now());
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();

    return unsubscribe;
  }, [channel]);

  const refetch = useCallback(async () => {
    if (!managerRef.current) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const newData = await managerRef.current.fetchData(channel, fallbackFetch);
      if (newData) {
        setData(newData);
        setLastUpdate(Date.now());
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refetch data');
    } finally {
      setLoading(false);
    }
  }, [channel, fallbackFetch]);

  return {
    data,
    loading,
    error,
    lastUpdate,
    refetch
  };
};

// Hook for connection status
export const useConnectionStatus = () => {
  const [status, setStatus] = useState<ConnectionStatus>({
    isConnected: false,
    lastHeartbeat: 0,
    reconnectAttempts: 0
  });

  useEffect(() => {
    const manager = RealTimeDataManager.getInstance();
    
    const unsubscribe = manager.subscribe<ConnectionStatus>('connection-status', (newStatus) => {
      setStatus(newStatus);
    });

    // Get initial status
    setStatus(manager.getConnectionStatus());

    return unsubscribe;
  }, []);

  return status;
};

export default RealTimeDataManager;